import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Database, Lock, EyeSlash } from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import useSeo from "@/lib/useSeo";

const POINTS = [
  {
    icon: EyeSlash,
    title: "We don't train AI on your project data",
    body: "Your uploads and AI chats are processed by leading third-party AI providers — the same enterprise-grade engines used by most modern SaaS products. We use their enterprise API tiers, whose terms explicitly prohibit training on customer data. Nothing you submit through ARISSAI is used to train, fine-tune, or improve any AI model.",
  },
  {
    icon: Database,
    title: "We don't keep your data longer than needed",
    body: "We store only what's required to display your workspace back to you: your account profile, your project items (assumptions, risks, issues, solutions, insights), and the AI outputs we generated for you. Raw uploaded files are processed in-memory and discarded after analysis — only the structured items you approve in the review screen are saved. No data brokering, no resale, no sharing.",
  },
  {
    icon: Lock,
    title: "Encrypted in transit and at rest",
    body: "All traffic uses TLS. Database and storage are encrypted at rest. Authentication uses bcrypt-hashed passwords and signed JWTs. Third-party AI calls travel directly over TLS to provider APIs and are not logged with their content.",
  },
  {
    icon: ShieldCheck,
    title: "Delete anytime",
    body: "Cancel from your Account page at any time. Email hello@arissai.com to permanently delete your workspace and all associated data within 30 days. No retention by default beyond what you actively keep in your projects.",
  },
];

export default function Privacy() {
  useSeo({
    title: "Privacy Policy · ARISSAI® · PIPEDA-aligned data handling",
    description: "How ARISSAI® collects, stores and processes your project data. PIPEDA-aligned. No third-party data sales. AI calls run through the Emergent universal LLM key with audit logging.",
    path: "/privacy",
  });
  return (
    <div className="min-h-screen bg-zinc-100" data-testid="privacy-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <BrandMark size={32} tagline="AI-Driven Project Intelligence" />
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950" data-testid="privacy-back-link">
            <ArrowLeft size={14} weight="bold" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020F5]">// Legal · Privacy Notice</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3">Your data stays yours.</h1>
        <p className="mt-6 text-zinc-600 text-base leading-relaxed">
          ARISSAI is built around a simple promise: the project information you bring into the platform is used to help you — and nothing else. No training, no resale, no long-term retention. We use industry-standard third-party AI engines under enterprise terms that prohibit training on customer data.
        </p>

        <div className="mt-12 grid gap-6">
          {POINTS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="bg-white border border-zinc-200 p-6" data-testid={`privacy-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 grid place-items-center bg-zinc-950 text-white shrink-0">
                    <Icon size={18} weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-display font-bold text-lg text-zinc-950">{p.title}</h2>
                    <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-zinc-950 text-white p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400">// In plain English</div>
          <p className="mt-3 text-sm leading-relaxed">
            We don't train AI on your data. We don't keep raw uploads — only the structured items you save into your projects. We use industry-standard third-party AI APIs (like every other AI SaaS) under enterprise terms that prohibit training. Questions? Email <a href="mailto:hello@arissai.com" className="text-white underline hover:text-[#9DA8FF]">hello@arissai.com</a>.
          </p>
        </div>

        <div className="mt-10 text-xs text-zinc-500 font-mono">Last updated: 2026-02-17 · ARISSAI Inc.</div>
      </main>
    </div>
  );
}
